// The `timer` block — time elapsed since `from`, ticking every second, coloured
// by thresholds that come from the page or (through tokens) from the row. The
// static site renders the value at render time and does not tick.

import React from 'react'
import type { TimerBlock } from '@twasta/page-schema'
import { usePageVarsSub } from '../renderers'
import { themeRem } from '../theme'
import { elapsedMs, elapsedTone, formatElapsed, TONE_COLOR, useNow } from './clock'

const SIZE: Record<NonNullable<TimerBlock['textSize']>, number> = { sm: 0.95, md: 1.35, lg: 2.2 }

export function TimerRenderer({ block, live = true }: { block: TimerBlock; live?: boolean }) {
  const s = usePageVarsSub()
  const from = s(block.from)
  const until = s(block.until)
  // A stopped clock (until set) never needs to tick.
  const stopped = !!(until && until.trim() && !until.includes('{{'))
  const now = useNow(live && !stopped ? 1000 : false)
  const ms = elapsedMs(from, now, stopped ? until : undefined)
  const unresolved = from.includes('{{')
  const tone = ms == null ? 'ok' : elapsedTone(ms, s(block.warnAfter), s(block.dangerAfter))
  const text = ms == null ? '—' : formatElapsed(ms, block.format || 'auto')
  const align = block.align || 'left'
  return (
    <div
      data-pg-timer={tone}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
      }}
    >
      {block.label ? (
        <span style={{ fontSize: themeRem(0.8), color: 'var(--pg-text-secondary)' }}>{s(block.label)}</span>
      ) : null}
      <span
        role="timer"
        aria-live="off"
        title={unresolved ? undefined : from}
        style={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 700,
          fontSize: themeRem(SIZE[block.textSize || 'md']),
          color: ms == null ? 'var(--pg-text-secondary)' : TONE_COLOR[tone],
        }}
      >
        {text}
      </span>
    </div>
  )
}
